import { supabase } from "@/lib/supabase";
import {
	ProfileEntityType,
	SupabaseTableEnum,
} from "@/lib/supabase/supabaseTypes";
import { StateCreator } from "zustand";
import { useCentralStore } from "../Central";
import { Logger } from "@/lib/logger/Logger";
import { getRole } from "../../lib/util";
import { useUserManagementStore } from ".";
import { produce } from "immer";
import { v4 as uuidv4 } from "uuid";
import { StorageBucketsEnum } from "@/dentlab/src/types/enums";
import { showNotification } from "../Central/selectors";
import { compressImage } from "@/lib/utils/compress-image";

export type Member = Partial<ProfileEntityType> & {
	allowed_signups_lab_id?: string | null;
	allowed_signups_lab_email?: string | null;
	allowed_signups_lab_created_at?: string | null;
	allowed_signups_lab_organization_id?: string | null;
	allowed_signups_lab_role?: string | null;
	role?: string | null;
	pending_invite_id?: number | null;
	is_pending_signup?: boolean;
};
export interface EmptyProfileData {
	id: string;
	code: string;
	first_name: string;
	last_name: string;
}

export interface ProfileData {
	code: string;
	first_name: string;
	last_name: string;
	avatar_url: string;
	location_id: number | null;
}

export interface UserManagementSlice {
	membersLookup: Record<string, Member>;
	getMembers: () => Promise<void>;
	getActiveMembers: (date: Date) => Member[];
	getEmptyProfile: (
		emptyProfileId: string
	) => Promise<EmptyProfileData | null>;
	updateEmptyProfile: (
		emptyProfileId: string,
		data: Partial<EmptyProfileData>
	) => Promise<boolean>;
	getProfile: (userId: string) => Promise<ProfileData | null>;
	updateProfile: (
		userId: string,
		data: Partial<ProfileData>
	) => Promise<boolean>;
	uploadProfileAvatar: (
		file: File,
		options?: { showNotification?: boolean }
	) => Promise<{ success: boolean; avatarPath?: string }>;
}

export const createUserManagementStore: StateCreator<UserManagementSlice> = (
	set
) => ({
	membersLookup: {},
	getMembers: async () => {
		const { organization, role, profile } = useCentralStore.getState();

		if (!organization || !profile) {
			Logger.error("No Organization set");
			return;
		}
		const organizationId = organization.id;
		if (!organizationId) return;
		const { data, error } = await supabase.rpc("get_members", {
			p_org_id: organizationId,
		});

		if (error) {
			Logger.error(error);
			return;
		}

		if (!organizationId) return;

		const { data: emptyProfilesData, error: emptyProfilesError } =
			await supabase
				.from(SupabaseTableEnum.EMPTY_PROFILES)
				.select()
				.eq("organization_id", organizationId);

		if (emptyProfilesError) {
			Logger.error(emptyProfilesError);
			return;
		}

		const emptyProfileIds = (emptyProfilesData ?? [])
			.map((d) => d.id)
			.filter((id): id is string => Boolean(id));

		const invitesByEmptyProfileId: Record<
			string,
			{ id: number; email: string; created_at: string }
		> = {};

		if (emptyProfileIds.length > 0) {
			const { data: pendingInvites, error: pendingInvitesError } =
				await supabase
					.from(SupabaseTableEnum.ALLOWED_SIGNUPS_LAB)
					.select("id, email, created_at, from_empty_profile_id")
					.in("from_empty_profile_id", emptyProfileIds);

			if (pendingInvitesError) {
				Logger.error(pendingInvitesError);
			} else {
				for (const invite of pendingInvites ?? []) {
					const key = (invite as any).from_empty_profile_id;
					if (key) {
						invitesByEmptyProfileId[key] = {
							id: (invite as any).id,
							email: (invite as any).email,
							created_at: (invite as any).created_at,
						};
					}
				}
			}
		}

		const fullProfiles = data.map((d) => ({
			id: d.id ?? d.allowed_signups_lab_id,
			first_name: d.first_name ?? "",
			last_name: d.last_name ?? "",
			code: d.code ?? "-",
			email: d.email ?? d.allowed_signups_lab_email,
			invited: d.allowed_signups_lab_created_at
				? new Date(d.allowed_signups_lab_created_at).toLocaleDateString(
						"de-ch"
					)
				: "-",
			role:
				d.id == profile.id
					? getRole(role as number)
					: getRole(d.allowed_signups_lab_role as number),
			accepted:
				d.email && d.email == d.allowed_signups_lab_email
					? new Date(d.created_at ?? "").toLocaleDateString("de-ch")
					: "-",
			deactivated_at: d.deactivated_at,
			location_id: (d as any).location_id ?? null,
			is_pending_signup: !d.id,
		}));

		const emptyProfiles = emptyProfilesData.map((d) => {
			const invite = invitesByEmptyProfileId[d.id];
			return {
				id: d.id,
				first_name: d.first_name ?? "",
				last_name: d.last_name ?? "",
				email: invite?.email ?? "",
				invited: invite?.created_at
					? new Date(invite.created_at).toLocaleDateString("de-ch")
					: "-",
				role: "Mitarbeiter ohne Profil",
				accepted: "-",
				code: d.code ?? "",
				deactivated_at: d.deactivated_at,
				pending_invite_id: invite?.id ?? null,
			};
		});

		const linkedInviteIds = new Set(
			Object.values(invitesByEmptyProfileId).map((invite) => invite.id)
		);

		set({
			membersLookup: {
				...fullProfiles.reduce((acc, curr) => {
					if (
						!curr.id ||
						(curr.email?.includes("support-") &&
							curr.email?.includes("@dentdesk.ch")) ||
						(curr.is_pending_signup &&
							linkedInviteIds.has(Number(curr.id)))
					) {
						return acc;
					}
					acc[curr.id] = curr;
					return acc;
				}, {} as any),
				...emptyProfiles.reduce(
					(acc, curr) => {
						if (!curr.id) {
							return acc;
						}
						acc[curr.id] = curr;
						return acc;
					},
					{} as Record<string, Member>
				),
			},
		});
	},
	getActiveMembers: (date: Date) => {
		const { membersLookup } = useUserManagementStore.getState();
		if (!membersLookup) {
			return [];
		}
		const normalizedDate = new Date(
			date.getFullYear(),
			date.getMonth(),
			date.getDate(),
			date.getHours(),
			date.getMinutes(),
			date.getSeconds(),
			date.getMilliseconds()
		);

		return Object.values(membersLookup).filter((member) => {
			if (!member.deactivated_at) return true;

			const deactivationDate = new Date(member.deactivated_at);
			const normalizedDeactivationDate = new Date(
				deactivationDate.getFullYear(),
				deactivationDate.getMonth(),
				deactivationDate.getDate(),
				deactivationDate.getHours(),
				deactivationDate.getMinutes(),
				deactivationDate.getSeconds(),
				deactivationDate.getMilliseconds()
			);

			return normalizedDeactivationDate >= normalizedDate;
		});
	},
	getEmptyProfile: async (emptyProfileId: string) => {
		const { data, error } = await supabase
			.from(SupabaseTableEnum.EMPTY_PROFILES)
			.select("id, code, first_name, last_name")
			.eq("id", emptyProfileId)
			.single();

		if (error) {
			Logger.error(error, {}, "getEmptyProfile");
			return null;
		}

		return {
			id: data.id,
			code: data.code || "",
			first_name: data.first_name || "",
			last_name: data.last_name || "",
		};
	},
	updateEmptyProfile: async (
		emptyProfileId: string,
		data: Partial<EmptyProfileData>
	) => {
		const { error } = await supabase
			.from(SupabaseTableEnum.EMPTY_PROFILES)
			.update({
				code: data.code || null,
				first_name: data.first_name || null,
				last_name: data.last_name || null,
			})
			.eq("id", emptyProfileId);

		if (error) {
			Logger.error(error, {}, "updateEmptyProfile");
			return false;
		}

		return true;
	},
	getProfile: async (userId: string) => {
		const { data, error } = await supabase
			.from(SupabaseTableEnum.PROFILES)
			.select()
			.eq("id", userId)
			.single();

		if (error) {
			Logger.error(error, {}, "getProfile");
			return null;
		}

		return {
			code: data.code || "",
			first_name: data.first_name || "",
			last_name: data.last_name || "",
			avatar_url: data.avatar_url || "",
			location_id: data.location_id ?? null,
		};
	},
	updateProfile: async (userId: string, data: Partial<ProfileData>) => {
		const { error } = await supabase
			.from(SupabaseTableEnum.PROFILES)
			.update({
				...data,
				modified_by_user_at: new Date().toISOString(),
			})
			.eq("id", userId);

		if (error) {
			Logger.error(error, {}, "updateProfile");
			return false;
		}

		const { profile } = useCentralStore.getState();
		if (profile && profile.id === userId) {
			useCentralStore.setState(
				produce((state) => {
					if (state.profile) {
						Object.assign(state.profile, data);
					}
				})
			);
		}

		return true;
	},
	uploadProfileAvatar: async (
		file: File,
		options?: { showNotification?: boolean }
	) => {
		const { profile, organization } = useCentralStore.getState();
		const shouldNotify = options?.showNotification ?? true;

		if (!profile?.id || !organization?.id) {
			if (shouldNotify) {
				showNotification({
					message: "Fehler beim Hochladen des Profilbilds",
					type: "error",
				});
			}
			Logger.error(
				"Profile or organization not found",
				{},
				"uploadProfileAvatar"
			);
			return { success: false };
		}

		const avatarPath = `avatars/${uuidv4()}/avatar`;
		const compressedFile = await compressImage(file, 0.6);
		const { error: uploadError } = await supabase.storage
			.from(StorageBucketsEnum.PUBLIC_IMAGES)
			.upload(`${organization.id}/${avatarPath}`, compressedFile);

		if (uploadError) {
			if (shouldNotify) {
				showNotification({
					message: "Fehler beim Hochladen des Profilbilds",
					type: "error",
				});
			}
			Logger.error(uploadError, {}, "uploadProfileAvatar");
			return { success: false };
		}

		const { updateProfile } = useUserManagementStore.getState();
		const success = await updateProfile(profile.id, {
			avatar_url: avatarPath,
		});

		if (!success) {
			if (shouldNotify) {
				showNotification({
					message: "Fehler beim Aktualisieren des Profils",
					type: "error",
				});
			}
			return { success: false };
		}

		if (shouldNotify) {
			showNotification({
				message: "Profilbild erfolgreich aktualisiert",
				type: "success",
			});
		}

		return { success: true, avatarPath };
	},
});
